'use client';

import { getCurrentUser, loginUser, logoutUser, registerUser } from '@/lib/api/auth';
import type { AuthResponse, LoginCredentials, RegisterData } from '@/lib/types/auth.types';
import type { User } from '@/lib/types/user.types';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

export function useAuth() {
  const t = useTranslations('auth.errors');
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Load user data from API (already returns User type)
          const userData = await getCurrentUser();
          setUser(userData);
        } catch (err) {
          // If token is invalid, remove it
          console.error('Failed to load user:', err);
          localStorage.removeItem('token');
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    loadUser();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const response = await loginUser({
        email: credentials.email,
        password: credentials.password,
      });

      // Validate response structure
      if (!response || !response.user || !response.token) {
        throw new Error(t('invalidServerResponse'));
      }

      // Response already contains full User object
      setUser(response.user);
      localStorage.setItem('token', response.token);
      return {
        user: response.user,
        token: response.token,
        refreshToken: undefined,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      // Map RegisterData to API format (fullName -> name, and ensure userType is only 'client' | 'supplier')
      // RegisterData.userType can be 'client' | 'supplier' | 'admin', but API only accepts 'client' | 'supplier'
      let userType: 'client' | 'supplier' = 'client'; // Default to client
      if (data.userType === 'supplier') {
        userType = 'supplier';
      } else if (data.userType === 'admin') {
        userType = 'client'; // Convert admin to client for registration
      }

      const apiData = {
        name: data.fullName,
        email: data.email,
        password: data.password,
        userType: userType,
        phone: data.phoneNumber,
        companyName: data.companyName,
        commercialRegister: data.commercialReg,
      };

      // Debug: Log registration data
      console.log('🔍 Register API Data:', {
        userType: apiData.userType,
        email: apiData.email,
        hasCompanyName: !!apiData.companyName,
      });

      const response = await registerUser(apiData);

      // Validate response structure
      if (!response || !response.user || !response.token) {
        throw new Error(t('invalidServerResponse'));
      }

      // Response already contains full User object
      setUser(response.user);
      localStorage.setItem('token', response.token);
      return {
        user: response.user,
        token: response.token,
        refreshToken: undefined,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Try to logout from backend, but don't fail if it doesn't work
      await logoutUser();
    } catch (error) {
      // Log error but continue with local logout
      console.warn('⚠️ Logout API call failed, but continuing with local logout:', error);
    } finally {
      // Always clear local state regardless of API call success
      setUser(null);
      localStorage.removeItem('token');
      // Clear journey user type if it exists (for testing mode)
      if (typeof window !== 'undefined') {
        localStorage.removeItem('__journey_user_type__');
        localStorage.removeItem('__sidebar_user_type__');
        // Clear query parameter if exists
        const url = new URL(window.location.href);
        if (url.searchParams.has('__userType')) {
          url.searchParams.delete('__userType');
          window.history.replaceState({}, '', url.toString());
        }
      }
    }
  };

  // Method to update auth state from a response without making an API call
  // Returns true if successful, false otherwise
  const setAuthFromResponse = (response: AuthResponse): boolean => {
    try {
      if (!response || !response.user || !response.token) {
        console.error('Invalid auth response:', response);
        return false;
      }

      // Response already contains full User object
      setUser(response.user);
      localStorage.setItem('token', response.token);
      return true;
    } catch (error) {
      console.error('Failed to set auth from response:', error);
      return false;
    }
  };

  return { user, isLoading, login, register, logout, setAuthFromResponse };
}
